import { useCallback, useMemo, useRef, useState } from "react";

/* One server-paged list of options for NewCustomAsyncSelectDropdown:
   page one on first open, the next page each time the menu reaches its
   bottom, a fresh page one for every search and on Refresh.

   `fetchPage({ search, page, limit })` returns { options, hasNext }.

   Where it has got to lives in a ref, not in state. The dropdown fires
   its scroll handler many times per flick, all before a re-render, and
   a guard read from state lets every one of them through -- the same
   page fetched three times over. A generation number drops the answer
   to a search that has since been replaced.

   `pinned` is what the field already holds (the batch's own course on
   an edit). It is shown first even before any page has loaded, and it
   never counts as "loaded" -- so opening the menu still fetches page one
   instead of treating the one pinned option as a list already there. */
const usePagedOptions = (fetchPage, { limit = 20, pinned = [] } = {}) => {
  const [loaded, setLoaded] = useState([]);
  const [loading, setLoading] = useState(false);
  const st = useRef({ page: 0, hasMore: true, search: "", busy: false, gen: 0, started: false });
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  const run = useCallback(
    async (search, page) => {
      const s = st.current;
      const append = page > 1;
      if (append && (s.busy || !s.hasMore)) return;
      if (!append) s.gen += 1;
      const gen = s.gen;
      s.busy = true;
      s.started = true;
      setLoading(true);
      try {
        const res = await fetchRef.current({ search, page, limit });
        if (gen !== s.gen) return;
        const got = Array.isArray(res?.options) ? res.options : [];
        s.page = page;
        s.search = search;
        s.hasMore = Boolean(res?.hasNext);
        setLoaded((prev) => {
          const all = append ? [...prev, ...got] : got;
          const seen = new Set();
          return all.filter((o) => (seen.has(o.value) ? false : seen.add(o.value)));
        });
      } catch (error) {
        if (gen === s.gen) s.hasMore = false;
      } finally {
        if (gen === s.gen) {
          s.busy = false;
          setLoading(false);
        }
      }
    },
    [limit],
  );

  const onMenuOpen = useCallback(() => {
    if (!st.current.started) run("", 1);
  }, [run]);

  const onInputChange = useCallback(
    (text) => {
      const search = String(text || "").trim();
      if (st.current.started && search === st.current.search) return;
      run(search, 1);
    },
    [run],
  );

  const onMenuScrollToBottom = useCallback(() => {
    const s = st.current;
    // Before page one has landed there is no "next" -- asking now would
    // start the list at page two.
    if (!s.started || s.page === 0) return;
    run(s.search, s.page + 1);
  }, [run]);

  const refresh = useCallback(() => run("", 1), [run]);

  const pinnedKey = pinned.map((p) => p?.value).join("|");
  const options = useMemo(() => {
    const head = pinned.filter((p) => p && p.value);
    const ids = new Set(head.map((p) => p.value));
    return [...head, ...loaded.filter((o) => !ids.has(o.value))];
    // pinnedKey stands in for `pinned`, which is a new array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, pinnedKey]);

  return { options, isLoading: loading, onMenuOpen, onInputChange, onMenuScrollToBottom, refresh };
};

/* Whether a list endpoint has a page after `page`. The course, trainer
   and classroom endpoints say so as `hasNext`; others only give
   totalPages, or nothing, in which case a full page means "maybe". */
export const hasNextPage = (pagination, page, got, limit) => {
  if (typeof pagination?.hasNext === "boolean") return pagination.hasNext;
  if (typeof pagination?.hasNextPage === "boolean") return pagination.hasNextPage;
  const totalPages = Number(pagination?.totalPages);
  if (totalPages > 0) return page < totalPages;
  return got >= limit;
};

export default usePagedOptions;
